
import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { SingleImageUploader, EpisodeImagesUploader } from "@/components/webtoon/ImageUploader";
import { WebtoonForm, categories } from "@/components/webtoon/WebtoonForm";

export default function CreateWebtoon() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [episodeImages, setEpisodeImages] = useState<string[]>([]);
  const [episodeThumbnail, setEpisodeThumbnail] = useState("");
  const [userId, setUserId] = useState<string | null>(null);

  // Get current user
  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        setUserId(session.user.id);
      } else {
        toast("Você precisa estar logado para criar um webtoon.");
        navigate("/auth");
      }
    };
    
    getUser();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!coverImage || !title || !category || episodeImages.length === 0) {
      toast.error("Por favor, preencha todos os campos obrigatórios e adicione pelo menos uma imagem ao episódio.");
      return;
    }

    setIsLoading(true);
    try {
      if (!userId) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          toast("Você precisa estar logado para criar um webtoon.");
          navigate("/auth");
          return;
        }
        setUserId(session.user.id);
      }

      const { error } = await supabase.from("webtoons").insert({
        title,
        description,
        cover_image: coverImage,
        category,
        user_id: userId,
        episode_images: episodeImages,
        episode_thumbnail: episodeThumbnail,
      });

      if (error) throw error;

      toast.success("Webtoon criado com sucesso! Seu webtoon foi publicado e já está disponível para leitura.");
      navigate("/");
    } catch (error: any) {
      console.error("Erro ao criar webtoon:", error);
      toast.error(error.message || "Ocorreu um erro ao tentar criar seu webtoon. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEpisodeImagesUpload = (urls: string[]) => {
    setEpisodeImages(prev => [...prev, ...urls]);
  };

  const pageVariants = {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const sectionVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen">
      <Navigation />
      <motion.main 
        className="container mx-auto px-4 pt-24 pb-16 max-w-2xl"
        variants={pageVariants}
        initial="initial"
        animate="animate"
      >
        <motion.div variants={sectionVariants}>
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => navigate("/create")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>

          <h1 className="text-2xl font-bold mb-8">Criar novo Webtoon</h1>
        </motion.div>

        <motion.div variants={sectionVariants} className="space-y-6">
          <SingleImageUploader
            label="Capa"
            folder="webtoon-covers"
            userId={userId}
            onImageUpload={setCoverImage}
            imageUrl={coverImage}
            dimensions="436x436"
            disabled={isLoading}
          />

          <EpisodeImagesUploader
            userId={userId}
            onImagesUpload={handleEpisodeImagesUpload}
            episodeImages={episodeImages}
            disabled={isLoading}
          />

          <SingleImageUploader
            label="Thumbnail do Episódio"
            folder="webtoon-thumbnails"
            userId={userId}
            onImageUpload={setEpisodeThumbnail}
            imageUrl={episodeThumbnail}
            dimensions="160x151"
            disabled={isLoading}
          />

          <WebtoonForm
            title={title}
            setTitle={setTitle}
            description={description}
            setDescription={setDescription}
            category={category}
            setCategory={setCategory}
            isLoading={isLoading}
            onSubmit={handleSubmit}
          />
        </motion.div>
      </motion.main>
    </div>
  );
}
